import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchAdminPackages,
  fetchAdminPackagesMeta,
  type AdminPackage,
  type AdminPackageMeta,
} from "../api/adminApi";
import { getAuthToken } from "../auth";
import { packages as mockPackages } from "../data";
import type { Package } from "../types";

function mapApiPackage(row: AdminPackage): Package {
  const status =
    row.status === "active" || row.status === "draft" || row.status === "suspended"
      ? row.status
      : row.status === "expired"
        ? "suspended"
        : "draft";

  return {
    id: String(row.id),
    code: row.code,
    name: row.name,
    hospital: row.hospital || "—",
    company: row.company || "—",
    hospitalId: String(row.hospital_id || ""),
    companyId: String(row.company_id || ""),
    monthlyRate: row.monthly_rate_label || `${row.monthly_rate} USD`,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    includedActs: row.included_acts,
    excludedActs: row.excluded_acts,
    status,
  };
}

export function useAdminPackages() {
  const [packages, setPackages] = useState<Package[]>(mockPackages);
  const [meta, setMeta] = useState<AdminPackageMeta>({ hospitals: [], companies: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setPackages(mockPackages);
      setMeta({ hospitals: [], companies: [] });
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const [list, formMeta] = await Promise.all([
        fetchAdminPackages(),
        fetchAdminPackagesMeta(),
      ]);
      setPackages(list.packages.map(mapApiPackage));
      setMeta(formMeta);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger les packages.");
      setPackages(mockPackages);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const hospitalFilterOptions = useMemo(
    () => [
      { value: "", label: "Tous les hôpitaux" },
      ...meta.hospitals.map((h) => ({ value: String(h.id), label: h.name })),
    ],
    [meta.hospitals],
  );

  const companyFilterOptions = useMemo(
    () => [
      { value: "", label: "Toutes les entreprises" },
      ...meta.companies.map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [meta.companies],
  );

  return {
    packages,
    meta,
    loading,
    error,
    usingApi,
    hospitalFilterOptions,
    companyFilterOptions,
    reload: load,
  };
}
